import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/config';
import toast from 'react-hot-toast';

interface GoogleAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface GoogleAuthUrlResponse {
  auth_url: string;
}

export const useGoogleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [processedCodes, setProcessedCodes] = useState<Set<string>>(new Set());
  const { loginWithTokens } = useAuth();

  // Get Google OAuth URL
  const getGoogleAuthUrl = async (): Promise<GoogleAuthUrlResponse> => {
    const apiUrl = getApiUrl();
    console.log('🔍 Google OAuth - Using API URL:', apiUrl);
    const response = await fetch(`${apiUrl}/api/v1/auth/google/url`);
    if (!response.ok) {
      throw new Error('Failed to get Google auth URL');
    }
    return response.json();
  };

  // Handle OAuth callback
  const handleGoogleCallback = async (code: string): Promise<GoogleAuthResponse> => {
    const apiUrl = getApiUrl();
    console.log('🔍 Google OAuth Callback - Using API URL:', apiUrl);
    const response = await fetch(`${apiUrl}/api/v1/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Google authentication failed');
    }

    return response.json();
  };

  // Mutation for Google OAuth callback
  const googleCallbackMutation = useMutation({
    mutationFn: handleGoogleCallback,
    onSuccess: async (data) => {
      try {
        console.log('🎉 Google OAuth success! Received tokens:', {
          hasAccessToken: !!data.access_token,
          hasRefreshToken: !!data.refresh_token,
          tokenType: data.token_type
        });
        
        // Store tokens directly first
        console.log('📦 Storing tokens in localStorage...');
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        
        // Verify storage
        const stored = {
          access: localStorage.getItem('access_token'),
          refresh: localStorage.getItem('refresh_token')
        };
        console.log('✅ Tokens stored verification:', {
          accessStored: !!stored.access,
          refreshStored: !!stored.refresh
        });
        
        // Use the auth context to update user state
        console.log('🔄 Updating auth context...');
        await loginWithTokens(data.access_token, data.refresh_token);
        
        // Check if this is a new user (no previous login)
        const isNewUser = !localStorage.getItem('last_login_time');
        
        if (isNewUser) {
          // Track new user signup via Google OAuth
          const { trackSignup } = await import('../lib/metaPixel');
          trackSignup('google');
          toast.success('Welcome! Your account has been created successfully.');
        } else {
          toast.success('Successfully signed in with Google!');
        }
        
        // Store login time for future reference
        localStorage.setItem('last_login_time', new Date().toISOString());
        
        // Check if there's a pending tier signup
        const pendingTier = sessionStorage.getItem('pending_tier_signup');
        if (pendingTier) {
          const { tierId, billingCycle } = JSON.parse(pendingTier);
          sessionStorage.removeItem('pending_tier_signup');
          console.log('🚀 Redirecting to checkout for tier:', tierId);
          window.location.href = `/pricing?checkout=true&tier=${tierId}&billing=${billingCycle || 'monthly'}`;
        } else {
          // Navigate to dashboard
          console.log('🚀 Redirecting to dashboard...');
          window.location.href = '/app/dashboard';
        }
      } catch (error) {
        console.error('❌ Failed to complete Google sign-in:', error);
        toast.error('Failed to complete sign-in');
        window.location.href = '/login';
      }
    },
    onError: (error: Error) => {
      console.error('❌ Google OAuth error:', error);
      
      // Only show error and redirect if it's a real failure
      // Don't show toast for backend errors if login ultimately succeeds
      window.location.href = '/login';
    },
  });

  // Start Google OAuth flow
  const signInWithGoogle = async (tierId?: string, billingCycle?: string) => {
    try {
      console.log('🚀 Starting Google OAuth flow...');
      setIsLoading(true);
      
      // Store tier info in sessionStorage for retrieval after OAuth
      if (tierId) {
        sessionStorage.setItem('pending_tier_signup', JSON.stringify({ tierId, billingCycle }));
      }
      
      console.log('📡 Getting Google auth URL...');
      const { auth_url } = await getGoogleAuthUrl();
      console.log('✅ Got auth URL:', auth_url);
      
      // Redirect to Google OAuth
      console.log('🔄 Redirecting to Google...');
      window.location.href = auth_url;
    } catch (error) {
      console.error('❌ Failed to start Google OAuth:', error);
      toast.error('Failed to start Google sign-in');
      setIsLoading(false);
    }
  };

  // Handle OAuth callback (call this from callback page)
  const handleCallback = useCallback((code: string) => {
    // Prevent processing the same code multiple times
    if (processedCodes.has(code)) {
      console.log('⏭️ Code already processed, skipping...');
      return;
    }
    
    console.log('🔄 Processing authorization code...');
    setProcessedCodes(prev => new Set(prev).add(code));
    googleCallbackMutation.mutate(code);
  }, [processedCodes, googleCallbackMutation]);

  return {
    signInWithGoogle,
    handleCallback,
    isLoading: isLoading || googleCallbackMutation.isPending,
    error: googleCallbackMutation.error,
  };
};